/**
 * A global primitive's render, shown.
 *
 * Every asset in the system is stored as a **path**; the URL is minted per session and never
 * written down. These particular paths sit under `renders/`, which the Storage rules make
 * world-readable because the primitive they belong to is — a curated global belongs to nobody
 * and its picture is on a public shelf.
 *
 * This app can read no other tree, and nothing here would open one: an author's render lives
 * under `authors/{aid}/renders/…`, which the rules close to everyone but that author.
 */

import { getDownloadURL, ref } from 'firebase/storage';
import { useEffect, useState } from 'react';
import type { JSX } from 'react';

import { storage } from '../firebase.js';

export function RenderImage({
  path,
  alt,
}: {
  path: string | undefined;
  alt: string;
}): JSX.Element | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (path === undefined) {
      setUrl(null);
      return;
    }
    let live = true;
    getDownloadURL(ref(storage(), path))
      .then((next) => {
        if (live) setUrl(next);
      })
      .catch(() => {
        if (live) setUrl(null);
      });
    return () => {
      live = false;
    };
  }, [path]);

  if (url === null) return null;
  return <img src={url} alt={alt} style={{ objectFit: 'cover' }} />;
}
